import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useRolePerms } from '../features/auth/useRolePerms.js';
import { AddSupplierModal } from '../components/domain/AddSupplierModal.js';
import { UploadModal } from '../components/domain/UploadModal.js';
import { downloadCSV } from '../lib/csv.js';
import { useResetSub } from '../features/filters/useFilters.js';
import { avEl } from '../lib/workerBadges.js';
import { waLink } from '../lib/wa.js';
import { WaIcon } from '../components/primitives/WaIcon.js';

interface SupplierContact {
  id: string;
  name: string;
  role: string | null;
  phone: string | null;
  email: string | null;
}

interface Supplier {
  id: string;
  name: string;
  category: string;
  tags: string[];
  compliance: 'compliant' | 'non-compliant' | 'pending';
  phone: string | null;
  email: string | null;
  address: string | null;
  photo: string | null;
  contacts: SupplierContact[];
}

type Compliance = 'compliant' | 'pending' | 'non-compliant';
type KpiFilter = Compliance | 'preferred';

function compBdg(c: Compliance) {
  const map: Record<Compliance, { bg: string; fg: string; lbl: string }> = {
    compliant: { bg: 'rgba(34,197,94,.15)', fg: '#22C55E', lbl: 'Compliant' },
    'non-compliant': { bg: 'rgba(239,68,68,.18)', fg: '#EF4444', lbl: 'Non-Compliant' },
    pending: { bg: 'rgba(245,158,11,.18)', fg: '#F59E0B', lbl: 'Pending' },
  };
  const m = map[c];
  return <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 4, background: m.bg, color: m.fg, fontWeight: 700 }}>{m.lbl}</span>;
}

function isPreferred(s: Supplier): boolean {
  return Array.isArray(s.tags) && s.tags.includes('preferred');
}

function lastTwoAddressSegments(addr: string | null): string {
  if (!addr) return '';
  return addr.split(',').slice(-2).join(',').trim();
}

interface SharedDocLite { id: string; name: string; createdAt: string; recipients: Array<{ contactId?: string | null; supplierContactId?: string | null; status: string; signedAt: string | null; declineReason?: string | null }> }

export function Suppliers() {
  const { data: supplierDocs = [] } = useQuery({ queryKey: ['shared-docs'], queryFn: () => api.get<SharedDocLite[]>('/shared-docs').then((r) => r.data) });
  const { canImport, canExport, canEditSuppliers } = useRolePerms();
  const [tab, setTab] = useState<string>('All');
  const [kpiFilter, setKpiFilter] = useState<KpiFilter | null>(null);
  const [search, setSearch] = useState('');
  const [activeId, setActiveId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState<Supplier | null>(null);

  useResetSub(() => { setTab('All'); setKpiFilter(null); setSearch(''); });

  const { data: suppliers = [] } = useQuery({
    queryKey: ['suppliers'],
    queryFn: () => api.get<Supplier[]>('/suppliers').then((r) => r.data),
  });

  const compliantCount = suppliers.filter((s) => s.compliance === 'compliant').length;
  const pendingCount = suppliers.filter((s) => s.compliance === 'pending').length;
  const nonCompliantCount = suppliers.filter((s) => s.compliance === 'non-compliant').length;
  const preferredCount = suppliers.filter(isPreferred).length;

  const kpiTiles: Array<{ key: KpiFilter; label: string; val: number; color: string }> = [
    { key: 'compliant', label: 'Compliant', val: compliantCount, color: '#22C55E' },
    { key: 'pending', label: 'Pending', val: pendingCount, color: '#F59E0B' },
    { key: 'non-compliant', label: 'Non-Compliant', val: nonCompliantCount, color: '#EF4444' },
    { key: 'preferred', label: 'Preferred', val: preferredCount, color: '#E8A427' },
  ];

  const categories = useMemo(() => ['All', ...Array.from(new Set(suppliers.map((s) => s.category).filter(Boolean)))], [suppliers]);

  const filtered = useMemo(() => {
    let r = [...suppliers];
    if (kpiFilter) {
      r = kpiFilter === 'preferred' ? r.filter(isPreferred) : r.filter((s) => s.compliance === kpiFilter);
    } else if (tab !== 'All') {
      r = r.filter((s) => s.category === tab);
    }
    const q = search.toLowerCase().trim();
    if (q) r = r.filter((x) => [x.name, x.category, x.address ?? '', x.compliance].join(' ').toLowerCase().includes(q));
    return r;
  }, [suppliers, kpiFilter, tab, search]);

  const active = activeId ? suppliers.find((s) => s.id === activeId) ?? null : null;

  function clickKpi(k: KpiFilter) {
    setKpiFilter((cur) => (cur === k ? null : k));
    setTab('All');
  }

  function clickTab(c: string) {
    setTab(c);
    setKpiFilter(null);
  }

  return (
    <div className="page" id="page-suppliers">
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, overflowY: 'auto', maxHeight: 'calc(100vh - 90px)' }}>
          <div className="hbar">
            <div>
              <div className="ph">Suppliers</div>
              <div className="ps" id="sup-sub">{suppliers.length} suppliers</div>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {canImport ? <button className="btn btn-ghost btn-sm" onClick={() => setImportOpen(true)}>⬆ Import</button> : null}
              {canExport ? <button className="btn btn-ghost btn-sm" onClick={() => downloadCSV('suppliers.csv', filtered as any, [
                { key: 'name', label: 'Name' },
                { key: 'category', label: 'Category' },
                { key: 'compliance', label: 'Compliance' },
                { key: 'phone', label: 'Phone' },
                { key: 'email', label: 'Email' },
                { key: 'address', label: 'Address' },
              ])}>⬇ Export</button> : null}
              {canEditSuppliers ? <button className="btn btn-primary" onClick={() => setAddOpen(true)}>+ Add Supplier</button> : null}
            </div>
          </div>
          <div className="g4" style={{ marginBottom: 16 }} id="sup-kpis">
            {kpiTiles.map((k) => (
              <div
                key={k.key}
                className="kpi"
                style={{ borderTopColor: k.color, cursor: 'pointer', outline: kpiFilter === k.key ? `1px solid ${k.color}` : undefined }}
                onClick={() => clickKpi(k.key)}
                title={`Click to filter by ${k.label}`}
              >
                <div className="lbl">{k.label}</div>
                <div className="val" style={{ color: k.color, fontSize: 18 }}>{k.val}</div>
                <div className="sub" style={{ color: k.color, fontSize: 9 }}>click to filter</div>
              </div>
            ))}
          </div>
          <div className="tab-bar" id="sup-tabs">
            {categories.map((cat) => (
              <div key={cat} className={`tab ${!kpiFilter && tab === cat ? 'active' : ''}`} onClick={() => clickTab(cat)}>{cat}</div>
            ))}
          </div>
          <div className="tbl-search" style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, padding: '5px 10px', margin: '6px 0 12px' }}>
            <span style={{ color: 'var(--faint)' }}>⌕</span>
            <input placeholder="Search suppliers…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ background: 'none', border: 'none', outline: 'none', color: 'var(--text)', fontSize: 12, flex: 1 }} />
          </div>
          <div className="g2" id="sup-grid">
            {filtered.map((s) => (
              <div key={s.id} className={`sup-card ${s.compliance === 'non-compliant' ? 'nc' : ''}`} onClick={() => setActiveId(s.id)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
                    {avEl({ name: s.name, photo: s.photo }, 28, 10)}
                    <div style={{ fontWeight: 600, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</div>
                  </div>
                  {compBdg(s.compliance)}
                </div>
                <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 6 }}>
                  {s.category}{s.address ? ` · ${lastTwoAddressSegments(s.address)}` : ''}
                </div>
                <div>{isPreferred(s) ? <span className="badge b-preferred">★ Preferred</span> : null}</div>
              </div>
            ))}
            {filtered.length === 0 ? (
              <div style={{ gridColumn: '1 / -1', color: 'var(--muted)', fontSize: 12, textAlign: 'center', padding: 30 }}>No suppliers match this filter.</div>
            ) : null}
          </div>
        </div>
        <div className="dp" id="sup-dp" style={{ display: active ? 'block' : 'none', maxHeight: 'calc(100vh - 90px)' }}>
          {active ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 11, minWidth: 0, flex: 1 }}>
                  {avEl({ name: active.name, photo: active.photo }, 42, 14)}
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{active.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{active.category}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
                  {canEditSuppliers ? <button className="btn btn-primary btn-sm" onClick={() => setEditing(active)}>✏ Edit</button> : null}
                  <button className="close-btn" onClick={() => setActiveId(null)}>×</button>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 11 }}>
                {compBdg(active.compliance)}
                {isPreferred(active) ? <span className="badge b-preferred">★ Preferred</span> : null}
              </div>
              <div className="divider"></div>
              {active.address ? <div className="dp-row"><span className="dp-key">Address</span><span className="dp-val" style={{ fontSize: 11, color: 'var(--muted)' }}>{active.address}</span></div> : null}
              {active.phone ? <div className="dp-row"><span className="dp-key">Phone</span><span className="dp-val"><a href={`tel:${active.phone}`} style={{ color: 'var(--text)', textDecoration: 'none', fontSize: 12 }}>{active.phone}</a></span></div> : null}
              {active.email ? <div className="dp-row"><span className="dp-key">Email</span><span className="dp-val"><a href={`mailto:${active.email}`} style={{ color: 'var(--info)', fontSize: 12 }}>{active.email}</a></span></div> : null}
              <div className="divider"></div>
              <div className="sec-title">Contacts ({active.contacts.length})</div>
              {active.contacts.length === 0 ? (
                <div style={{ fontSize: 11, color: 'var(--muted)' }}>No contacts on record</div>
              ) : (
                active.contacts.map((sc) => {
                  const firstName = sc.name.split(' ')[0] ?? sc.name;
                  return (
                    <div
                      key={sc.id}
                      style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 9, padding: 11, marginBottom: 8, transition: 'border-color .15s' }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--accent)'; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--border)'; }}
                    >
                      <div style={{ display: 'flex', gap: 9, alignItems: 'center' }}>
                        {avEl(sc.name, 30, 11)}
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 600 }}>{sc.name}</div>
                          {sc.role ? <div style={{ fontSize: 10, color: 'var(--muted)' }}>{sc.role}</div> : null}
                        </div>
                      </div>
                      {(() => {
                        const rows = supplierDocs.flatMap((d) => d.recipients.filter((r) => r.supplierContactId === sc.id).map((r) => ({ id: d.id, name: d.name, status: r.status, signedAt: r.signedAt, declineReason: r.declineReason ?? null })));
                        return rows.length === 0 ? null : (
                          <div className="supplier-docs" style={{ marginTop: 8 }}>
                            {rows.map((d) => (
                              <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, fontSize: 10, padding: '4px 0', borderTop: '1px solid var(--border)' }}>
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📄 {d.name}{d.declineReason ? ` · "${d.declineReason}"` : ''}</span>
                                {d.status === 'signed' ? <span className="sign-chip-signed">✓ SIGNED</span> : d.status === 'declined' ? <span className="sign-chip-pending">✕ DECLINED</span> : <span className="sign-chip-pending">⏱ PENDING</span>}
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                      <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {sc.phone ? <a href={`tel:${sc.phone}`} onClick={(e) => e.stopPropagation()} className="btn btn-ghost btn-xs" style={{ fontSize: 10 }}>📞 {sc.phone}</a> : null}
                        {sc.email ? <a href={`mailto:${sc.email}`} onClick={(e) => e.stopPropagation()} className="btn btn-info btn-xs" style={{ fontSize: 10 }}>✉ {sc.email}</a> : null}
                        {sc.phone ? (
                          <a
                            href={waLink(sc.phone, `Hi ${firstName}, this is CLS. `)}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="btn btn-wa btn-xs"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                          >
                            <WaIcon size={11} /> WhatsApp
                          </a>
                        ) : null}
                      </div>
                    </div>
                  );
                })
              )}
              <div className="divider"></div>
              <div className="sec-title">Compliance Questionnaire</div>
              {(() => {
                const c = active.compliance;
                const items: Array<{ q: string; ok: boolean }> = [
                  { q: 'Insurance Certificate', ok: c === 'compliant' },
                  { q: 'H&S Policy', ok: c !== 'non-compliant' },
                  { q: 'ISO 9001', ok: c === 'compliant' },
                  { q: 'Environmental Policy', ok: c === 'compliant' },
                  { q: 'Modern Slavery Statement', ok: c === 'compliant' },
                  { q: 'GDPR Compliance', ok: c === 'compliant' },
                ];
                return items.map((item) => (
                  <div key={item.q} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '7px 0', borderBottom: '1px solid rgba(30,42,64,.4)' }}>
                    <span style={{ color: item.ok ? 'var(--live)' : 'var(--danger)', fontSize: 16, lineHeight: 1 }}>{item.ok ? '✓' : '✗'}</span>
                    <span style={{ fontSize: 12, flex: 1 }}>{item.q}</span>
                    <span className={`badge ${item.ok ? 'b-valid' : 'b-expired'}`} style={{ fontSize: 9 }}>{item.ok ? 'Pass' : 'Fail'}</span>
                  </div>
                ));
              })()}
              {canEditSuppliers ? (
                <div style={{ marginTop: 14 }}>
                  <button className="btn btn-ghost btn-sm" style={{ width: '100%', justifyContent: 'center' }} onClick={() => setEditing(active)}>✏ Edit Supplier Details</button>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </div>
      {addOpen ? <AddSupplierModal onClose={() => setAddOpen(false)} /> : null}
      {editing ? <AddSupplierModal onClose={() => setEditing(null)} initial={editing} /> : null}
      {importOpen ? <UploadModal entity="suppliers" onClose={() => setImportOpen(false)} /> : null}
    </div>
  );
}
